/** TodosPage.tsx — 일일 업무 메인 페이지 */
import { useState, useEffect, useCallback } from 'react';
import { C, T, R } from '../components/ui';
import * as todosApi from '../api-todos';
import type { Todo } from '../types';
import TodoCard from '../components/TodoCard';
import TodoDetailDrawer from '../components/TodoDetailDrawer';
import TodoCreateModal from '../components/TodoCreateModal';
import type { Staff } from '../components/AssigneeChipPicker';

interface Props {
  myId: string;
  onToast?: (msg: string, kind?: 'success' | 'error') => void;
}

type StatusFilter = 'all' | 'open' | 'done';

export default function TodosPage({ myId, onToast }: Props) {
  const [userFilter, setUserFilter] = useState<string>('all');
  const [status, setStatus] = useState<StatusFilter>('open');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<Todo[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ pending: 0, partial: 0, review: 0, notify: 0, done: 0, total: 0 });
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [recurringOpen, setRecurringOpen] = useState(false);
  const [page, setPage] = useState(1);
  const LIMIT = 30;

  useEffect(() => {
    todosApi.getStaff().then(r => { if (r.success && r.data) setStaff(r.data); });
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const f: todosApi.ListFilters = { status, search, page, limit: LIMIT };
      if (userFilter !== 'all') f.user_id = userFilter;
      const r = await todosApi.getTodos(f);
      if (r.success && r.data) {
        setItems(r.data.items);
        setTotal(r.data.total);
        if ((r.data as any).stats) setStats((r.data as any).stats);
      }
    } finally { setLoading(false); }
  }, [userFilter, status, search, page]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { setPage(1); }, [userFilter, status, search]);

  function handleChange(t: Todo) { setItems(prev => prev.map(x => x.id === t.id ? t : x)); }
  function handleDelete(id: string) { setItems(prev => prev.filter(x => x.id !== id)); }

  const totalPages = Math.ceil(total / LIMIT) || 1;

  // 직원 정렬: 본인 → 가나다순
  const sortedStaff = [...staff].sort((a, b) => {
    if (a.id === myId) return -1;
    if (b.id === myId) return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div style={{ padding: 18, overflow: 'auto', height: '100%' }}>
      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
        <Kpi label="진행 중" value={stats.pending + stats.partial}
          color={C.accent} bg={C.accentLt} icon="⚡" />
        <Kpi label="확인 · 안내 남음" value={(stats.review || 0) + (stats.notify || 0)}
          color={C.purple} bg={C.purpleLt} icon="↻" />
        <Kpi label="완료" value={stats.done}
          color={C.green} bg={C.greenLt} icon="✓" />
        <Kpi label="전체" value={stats.total}
          color={C.sub} bg={C.bgAlt} icon="📋" />
      </div>

      {/* 필터 한 줄 (담당자 + 상태 + 검색 + 등록) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14, alignItems: 'center' }}>
        <span style={{ fontSize: T.xs, color: C.muted, fontWeight: 600, marginRight: 2 }}>담당자</span>
        <FilterChip act={userFilter === 'all'} onClick={() => setUserFilter('all')}>전체</FilterChip>
        {sortedStaff.map(s => (
          <FilterChip key={s.id} act={userFilter === s.id} onClick={() => setUserFilter(s.id)}>
            {s.name}{s.id === myId ? ' (나)' : ''}
          </FilterChip>
        ))}
        <span style={{ width: 1, height: 18, background: C.border, margin: '0 6px' }} />
        <span style={{ fontSize: T.xs, color: C.muted, fontWeight: 600, marginRight: 2 }}>상태</span>
        <FilterChip act={status === 'all'} onClick={() => setStatus('all')}>전체</FilterChip>
        <FilterChip act={status === 'open'} onClick={() => setStatus('open')}>진행 중</FilterChip>
        <FilterChip act={status === 'done'} onClick={() => setStatus('done')}>완료</FilterChip>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍 검색"
          style={{ marginLeft: 'auto', padding: '6px 10px', borderRadius: R.sm,
            boxSizing: 'border-box',
            border: `1px solid ${C.border}`, fontSize: T.sm, width: 160,
            fontFamily: 'inherit', lineHeight: 1.4 }} />
        <button onClick={() => setCreateOpen(true)}
          style={{ padding: '6px 14px', borderRadius: R.sm, border: 'none',
            background: C.accent, color: C.white, fontSize: T.sm, fontWeight: 700,
            cursor: 'pointer', whiteSpace: 'nowrap' }}>
          ＋ 새 일일 업무
        </button>
        <button onClick={() => setRecurringOpen(true)}
          style={{ padding: '6px 12px', borderRadius: R.sm,
            border: `1px solid ${C.border}`, background: C.white,
            color: C.sub, fontSize: T.sm, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          🔁 반복 일정
        </button>
      </div>

      {/* 카드 목록 */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: C.muted }}>⏳ 로딩 중...</div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 50, color: C.muted, background: C.bgAlt,
          borderRadius: R.md, border: `1px dashed ${C.border}` }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
          <div style={{ fontSize: T.sm }}>
            {status === 'done' ? '완료된 항목 없음' : '일일 업무 없음 — 위 등록 버튼으로 추가하세요'}
          </div>
        </div>
      ) : (
        items.map(t => (
          <TodoCard key={t.id} todo={t} staff={staff} myId={myId}
            onChange={handleChange} onDelete={handleDelete}
            onOpenDetail={(id) => setDetailId(id)} onToast={onToast} />
        ))
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 14 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={pgBtnStyle(page === 1)}>← 이전</button>
          <div style={{ padding: '5px 11px', fontSize: T.sm, color: C.sub }}>{page} / {totalPages}</div>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={pgBtnStyle(page === totalPages)}>다음 →</button>
        </div>
      )}

      {detailId && (
        <TodoDetailDrawer todoId={detailId} initial={items.find(x => x.id === detailId)} staff={staff} myId={myId}
          onClose={() => setDetailId(null)}
          onUpdated={handleChange} onDeleted={handleDelete} onToast={onToast} />
      )}

      {createOpen && (
        <TodoCreateModal myId={myId} staff={staff}
          onClose={() => setCreateOpen(false)}
          onCreated={reload}
          onToast={onToast} />
      )}

      {recurringOpen && (
        <RecurringListModal staff={staff} onClose={() => setRecurringOpen(false)} onToast={onToast} />
      )}
    </div>
  );
}

function pgBtnStyle(disabled: boolean) {
  return { padding: '5px 11px', borderRadius: R.sm, border: `1px solid ${C.border}`,
    background: C.white, color: disabled ? C.muted : C.sub, fontSize: T.sm,
    cursor: disabled ? 'default' : 'pointer' } as const;
}

function Kpi({ label, value, color, bg, icon }: {
  label: string; value: number; color: string; bg: string; icon: string;
}) {
  return (
    <div style={{ flex: 1, padding: 14, background: C.white,
      border: `1px solid ${C.border}`, borderRadius: R.lg, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 2 }}>{label}</div>
          <div style={{ fontSize: 24, fontWeight: 800, color }}>{value}</div>
        </div>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: T.xl }}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function FilterChip({ act, onClick, children }: { act: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      style={{ padding: '4px 11px', borderRadius: R.sm,
        border: act ? `1.5px solid ${C.accent}` : `1px solid ${C.border}`,
        background: act ? C.accentLt : C.white,
        color: act ? C.accent : C.sub,
        fontSize: T.sm, fontWeight: act ? 700 : 500, cursor: 'pointer' }}>
      {children}
    </button>
  );
}

const DAY_KOR: Record<string, string> = {
  monday: '월', tuesday: '화', wednesday: '수', thursday: '목',
  friday: '금', saturday: '토', sunday: '일',
};

function RecurringListModal({ staff, onClose, onToast }: {
  staff: Staff[]; onClose: () => void; onToast?: (m: string, k?: 'success' | 'error') => void;
}) {
  const [items, setItems] = useState<Todo[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    todosApi.getTemplates().then(r => {
      if (r.success && r.data) setItems(r.data);
    }).finally(() => setLoading(false));
  }, []);
  async function del(id: string) {
    if (!window.confirm('삭제하시겠습니까? 이미 생성된 인스턴스는 유지됩니다.')) return;
    await todosApi.deleteTodo(id);
    setItems(prev => prev.filter(t => t.id !== id));
    onToast?.('삭제됨', 'success');
  }
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.white,
        borderRadius: R.md, padding: 20, width: '100%', maxWidth: 540,
        maxHeight: '85vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: T.lg, fontWeight: 800, color: C.text }}>🔁 반복 일정 관리</div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent',
            fontSize: 22, cursor: 'pointer', color: C.muted, padding: 4 }}>×</button>
        </div>
        <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 12 }}>
          새 반복 일정은 "+ 새 일일 업무" 버튼 → 모달에서 "🔁 반복 등록" 체크박스로 생성
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 30, color: C.muted }}>⏳ 로딩 중...</div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 30, color: C.muted, background: C.bgAlt,
            borderRadius: R.sm, border: `1px dashed ${C.border}` }}>
            등록된 반복 일정 없음
          </div>
        ) : items.map(t => {
          const list = Array.isArray(t.assignees) && t.assignees.length > 0 ? t.assignees : [t.assigned_to];
          const names = list.map(id => staff.find(s => s.id === id)?.name || '?').join(', ');
          const pattern = t.recur_type === 'daily' ? '매일' :
            t.recur_type === 'weekly' && t.recur_days ?
            `매주 ${t.recur_days.split(',').map(d => DAY_KOR[d.trim()] || '').filter(Boolean).join(',')}요일` : '';
          return (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 12px', background: C.white,
              border: `1px solid ${C.border}`, borderRadius: R.sm, marginBottom: 6 }}>
              <div style={{ fontSize: T.lg }}>🔁</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: T.sm, fontWeight: 600, color: C.text }}>{t.title}</div>
                <div style={{ fontSize: T.xs, color: C.muted }}>
                  {pattern} · 담당 {names}
                  {t.recur_last_at && ` · 마지막 ${t.recur_last_at}`}
                </div>
              </div>
              <button onClick={() => del(t.id)}
                style={{ padding: '5px 10px', borderRadius: R.sm,
                  border: `1px solid ${C.red}`, background: C.white,
                  color: C.red, fontSize: T.xs, cursor: 'pointer' }}>
                🗑
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
